import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {DeletedAttachment} from 'app/main/pages/events/editor-emitter.dto';
import {SigningServiceHttp} from '../signing-service';
import { Observable } from 'rxjs';
import { RoleDetail, CustomerStatusResponse } from './signing-page.dto';
import { Participants } from '../signing-sidebar/participants/participants.dto';

export interface SigningData {
  all_fields_data: object;
  recipient_fields_data: object;
  signature?: string;
  signature_type?: string;
  third_party_auth_response?: string;
  deleted_attachments?:DeletedAttachment[]
}

@Injectable({
  providedIn: 'root'
})
export class SingingPageService {

  constructor(
    private http: SigningServiceHttp,
    private httpClient: HttpClient
  ) {
  }
  
  public httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "multipart/form-data"
    })
  }

  /**
   * get the document detail for signing
   * @param role_id 
   * @returns 
   */
  getSignDocumentDetail(role_id: number): Observable<RoleDetail> {
    return this.http.get(`sign-document/${role_id}`);
  }
 
 
 
  getDocumentDetail(role_id: number) {
    return this.http.get(`sign-document/${role_id}/owner-document`, {'sender_area': '1'});
  }

  /**
   * Sign the document
   * @param role_id 
   * @param data:SigningData
   * @returns promise
   */
  signDocument(role_id: number, data: SigningData, document_id:number) {
    return this.http.post(`sign-document/${role_id}/${document_id}/process-signature`, data);
  }

  /**
   * prepate the document for sign
   * @param role_id 
   * @param data 
   * @returns 
   */
  prepareSigningData(role_id:number, data: SigningData, document_id:number) {
    const role_fields_data = this.filterObject(data.all_fields_data, "selected_for_role_id", String(role_id));
    return this.signDocument(role_id, {...data, ...{recipient_fields_data:role_fields_data}}, document_id);
  }

  /**
   * get the all assigned recipient fields
   * @param obj 
   * @param filter 
   * @param filterValue 
   * @returns object
   */
  filterObject(obj, filter, filterValue) {
    let field_data = {};
    for (const field in obj) {
      if (obj[field][filter] == filterValue || obj[field][filter] == "-1") {
        field_data[field] = obj[field];
      }
    }
    field_data = { ...field_data, ...{ blocks: obj['blocks'] } };
    return field_data;
  }

  /**
   * upload the file in the Google Cloud
   * @param formData 
   */
  uploadFile(role_id:number, document_id:number, formData, isEform:boolean = false) {
    const apiRoute = isEform ? 'eform-signing' : 'sign-document';
    return this.http.post(`${apiRoute}/${role_id}/${document_id}/attachment`, formData);
  }

  /**
   * pdf to img
   * @param role_id
   * @param document_id
   * @param formData
   */
  uploadPdf(role_id:number, document_id:number, formData, isEform:boolean = false) {
    const apiRoute = isEform ? 'eform-signing' : 'sign-document';
    return this.http.post(`${apiRoute}/${role_id}/${document_id}/document-upload-pdf`, formData);
  }

  /**
   * add the log
   * @param role_id 
   * @param formData 
   * @returns 
   */
  addLog(role_id:number, formData) {
    return this.http.post(`sign-document/${role_id}/activity-log`, formData);
  }

  /**
   * when the authentication method is readonly
   * @param role_id 
   * @returns 
   */
  readOnly(role_id:number){
    return this.http.post(`sign-document/${role_id}/read-only`,{});
  }

  
  /**
   * Retrieves the authorization URL based on the provided role ID, user ID, and whether it's an eForm request.
   * 
   * @param {number} roleId - The role ID associated with the user.
   * @param {number} id - The user or entity ID.
   * @param {boolean} [isEForm=false] - Flag indicating whether this is an eForm request (default is false).
   * @returns An observable of the HTTP GET request to the generated URL.
   */
  getAuthzingsecUrl(roleId: number, id: number, isEForm: boolean = false) {
      const apiUrl = isEForm ? `eform-nobankid/${id}/auth` : `nobankid/${roleId}/auth`;
      return this.http.get(apiUrl);
  }


  /**
   * Retrieves the authorization URL for MIT ID based on the provided role ID, user ID, and whether it's an eForm request.
   * 
   * @param {number} roleId - The role ID associated with the user.
   * @param {number} id - The user or entity ID.
   * @param {boolean} [isEForm=false] - Flag indicating whether this is an eForm request (default is false).
   * @returns An observable of the HTTP POST request to the generated URL.
  */
  getAuthzingsecMitIdUrl(roleId: number, id: number, isEForm: boolean = false) {
      const apiUrl = isEForm ? `eform-mitid/${id}/auth` : `mitid/${roleId}/auth`;
      return this.http.get(apiUrl);
  }


  /**
   * Retrieves the authentication URL for Finnish BankID based on the provided role or eForm ID.
   *
   * @param roleId - The role identifier used for authentication when not using eForm.
   * @param id - The identifier used for authentication when using eForm.
   * @param isEForm - Optional flag indicating whether to use the eForm authentication endpoint. Defaults to `false`.
   * @returns An Observable containing the authentication URL response from the API.
   */
  getAuthzingsecFinnishBankIdUrl(roleId: number, id: number, isEForm: boolean = false) {
      const apiUrl = isEForm ? `eform-finid/${id}/auth` : `finid/${roleId}/auth`;
      return this.http.get(apiUrl);
  }


  /**
   * Send request to sender for extension of due date
   * @param role_id 
   * @returns 
   */
  requestNewDueDate(role_id:number, message: string){
    return this.http.post(`sign-document/${role_id}/request-new-expiry`,{message: message});
  }

  apiFieldOptions(role_id:number, id:number){
    return this.http.get(`sign-document/${role_id}/${id}/field-options`);
  }

  getUploadedFiles(role_id:number){
    return this.http.get(`sign-document/${role_id}/additional-file`);
  }

  uploadedFile(role_id:number, formData){
    return this.http.post(`sign-document/${role_id}/additional-file`,formData);
  }

  updateRoleInformation(role_id: number, token:string, result){
    return this.httpClient.post(`sign-document/${role_id}/update-role-information`,result, {
      headers: new HttpHeaders({'token': token})
    })
  }

  /**
   * Check if the signer is an existing customer and get pending signers
   * Used for post-sign lead generation modal
   * @param role_id - The role ID of the signer
   * @returns Observable with customer status and pending signers
   */
  checkCustomerStatus(role_id: number): Observable<CustomerStatusResponse> {
    return this.http.get(`sign-document/${role_id}/check-customer-status`);
  }

  /**
   * Get participants for a document
   * @param role_id - The role ID
   * @param document_id - The document ID
   * @returns Observable with participants data including recipient_roles
   */
  getParticipants(role_id: number, document_id: number): Observable<Participants> {
    return this.http.get(`sign-document/${role_id}/${document_id}/participants`);
  }

  fetchProductTableProducts(role_id:number, document_id:number, isEform:boolean = false, page: number | string, query: string) {
    const apiRoute = isEform ? `eform-signing/${role_id}/${document_id}` : `sign-document/${role_id}`;
    return this.http.get(`${apiRoute}/products?page=${page}&q=${query}`);
  }
}
